import React, { useContext, useEffect } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { AuthContext } from '../../context/AuthContext'
import StepUpGate, {
  rememberCasesDestination,
  takeCasesDestination,
} from './StepUpGate'

// ======================================================================
// THE ROUTE GUARD
// ======================================================================
//
// Two gates, in order, and the order is the whole point:
//
//   1. IS THERE A KWERY SESSION AT ALL?  No -> /login.
//   2. HAS IDENTITY BEEN PROVED RECENTLY? No -> the code prompt, at
//      this same URL.
//
// Putting them the other way round would show a code prompt to somebody
// who is not logged in, which cannot be satisfied and which asks a
// stranger to type a code into a screen that names a module.
//
// ======================================================================
// WHY THIS IS NOT ProtectedRoute
// ======================================================================
//
// routes/ProtectedRoute.jsx does the same first job and is used by the
// other seventy-odd routes, but it redirects with
// `<Navigate to="/login" replace />` and records nothing about where the
// user was going. That is fine for a sidebar link — the user clicks it
// again afterwards. It is not fine here, because THE PEOPLE WHO NEED
// CASES MOST OFTEN ARRIVE BY LINK AND HAVE NO SIDEBAR ENTRY: a subject
// and a witness correctly never see the Cases icon, so a lost
// destination leaves them at /welcome with no way to reach the case
// they were sent, and nothing on screen to tell them one exists.
//
// So this guard stashes the path before redirecting. ProtectedRoute is
// left alone; it is used by every other route in the application and is
// not this task's to change.

export default function CasesRoute({ children }) {
  const { isAuthenticated, loading, silentRefresh } = useContext(AuthContext)
  const location = useLocation()
  const navigate = useNavigate()

  const here = `${location.pathname}${location.search}`

  // Stashed during RENDER of the redirect branch rather than in an
  // effect, because <Navigate> unmounts this component immediately and
  // an effect scheduled for after the commit may never run.
  if (!loading && !isAuthenticated) {
    rememberCasesDestination(here)
    return <Navigate to="/login" replace />
  }

  if (loading) {
    // AuthContext is still restoring the session from localStorage.
    // Deliberately the same neutral line ProtectedRoute uses, so the
    // Cases route does not flash something different on every load.
    return <div className="text-[#00FF99] p-8 text-center">Loading…</div>
  }

  return (
    <StepUpGate
      silentRefresh={silentRefresh}
      onSignedOut={() => {
        rememberCasesDestination(here)
        navigate('/login', { replace: true })
      }}
    >
      {children}
    </StepUpGate>
  )
}

/**
 * ======================================================================
 * THE DEEP LINK, SECOND HALF
 * ======================================================================
 *
 * Mounted once in App.js, inside the router and outside every route, so
 * it is watching whatever page the user lands on after logging in.
 *
 * THE PROBLEM IT SOLVES. LoginPage hands off to
 * utils/postLoginRedirect.js, which chooses between /superadmin,
 * /onboarding and /welcome from the user's own properties and has no
 * concept of an intended destination. It then navigates with
 * `window.location.href`, a full page load. So a subject who clicks
 * /cases/<id> in an email, logs in, and would otherwise arrive at
 * /welcome — the case they were sent nowhere in sight, and no Cases icon
 * in their sidebar to look for it with, because a subject correctly
 * never gets one.
 *
 * Neither of those files is in this task's authority, and both are used
 * by every other login path in the application. So the resumption is
 * done here instead: the destination was stashed in sessionStorage
 * (which survives the hard navigation, and is per-tab), and this
 * consumes it exactly once, the moment a session exists.
 *
 * IT ONLY EVER SENDS SOMEBODY TO /cases. rememberCasesDestination
 * refuses to store anything else, so a stale or tampered value cannot
 * turn this into an open redirect.
 */
export function CasesDeepLinkResume() {
  const { isAuthenticated, loading } = useContext(AuthContext)
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (loading || !isAuthenticated) return
    // Already there — nothing to resume, and consuming the value here
    // would swallow it before the user had arrived.
    if (location.pathname.startsWith('/cases')) return
    // The login screen itself is mid-flight. Waiting for the landing
    // page avoids racing LoginPage's own navigation.
    if (location.pathname === '/login') return

    const target = takeCasesDestination()
    if (target) navigate(target, { replace: true })
  }, [isAuthenticated, loading, location.pathname, navigate])

  return null
}
